import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  ListChecks,
  Play,
  ScanSearch,
  GitCompare,
  Search,
  CreditCard,
  Settings,
  Users,
  FileBarChart,
  ChevronsLeft,
  ChevronsRight,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useUIStore } from '@/store/uiStore';
import { cn } from '@/lib/utils';

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  section: 'main' | 'tools' | 'settings';
}

const navItems: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, section: 'main' },
  { to: '/sources', label: 'Sources', icon: FileText, section: 'main' },
  { to: '/criteria', label: 'Criteria', icon: ListChecks, section: 'main' },
  { to: '/evaluate', label: 'Evaluate', icon: Play, section: 'main' },
  { to: '/screening', label: 'Screening', icon: ScanSearch, section: 'main' },
  { to: '/search', label: 'Search', icon: Search, section: 'tools' },
  { to: '/compare', label: 'Compare', icon: GitCompare, section: 'tools' },
  { to: '/reports', label: 'Reports', icon: FileBarChart, section: 'tools' },
  { to: '/team', label: 'Team', icon: Users, section: 'settings' },
  { to: '/pricing', label: 'Pricing', icon: CreditCard, section: 'settings' },
  { to: '/settings', label: 'Settings', icon: Settings, section: 'settings' },
];

function NavLink({
  item,
  isActive,
  collapsed,
}: {
  item: NavItem;
  isActive: boolean;
  collapsed: boolean;
}) {
  const Icon = item.icon;

  const link = (
    <Link
      to={item.to}
      className={cn(
        'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
        'hover:bg-accent hover:text-accent-foreground',
        isActive
          ? 'bg-indigo-50 text-indigo-600 font-medium'
          : 'text-slate-600',
        collapsed && 'justify-center px-2'
      )}
    >
      <Icon className={cn('h-4 w-4 shrink-0', isActive && 'text-indigo-600')} />
      {!collapsed && <span>{item.label}</span>}
    </Link>
  );

  if (collapsed) {
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right" className="font-normal">
          {item.label}
        </TooltipContent>
      </Tooltip>
    );
  }

  return link;
}

function NavSection({
  label,
  items,
  pathname,
  collapsed,
}: {
  label: string;
  items: NavItem[];
  pathname: string;
  collapsed: boolean;
}) {
  return (
    <div className="space-y-1">
      {!collapsed && (
        <p className="px-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">
          {label}
        </p>
      )}
      {collapsed && <Separator className="my-2" />}
      {items.map((item) => (
        <NavLink
          key={item.to}
          item={item}
          isActive={pathname === item.to || pathname.startsWith(item.to + '/')}
          collapsed={collapsed}
        />
      ))}
    </div>
  );
}

export function Sidebar() {
  const location = useLocation();
  const { sidebarOpen, sidebarCollapsed, setSidebarOpen, setSidebarCollapsed } =
    useUIStore();

  const mainItems = navItems.filter((i) => i.section === 'main');
  const toolItems = navItems.filter((i) => i.section === 'tools');
  const settingsItems = navItems.filter((i) => i.section === 'settings');

  const sidebarContent = (
    <TooltipProvider>
      <div className="flex h-full flex-col">
        {/* Mobile close button */}
        <div className="flex items-center justify-end p-2 lg:hidden">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close sidebar</span>
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-6 px-3 py-4 overflow-y-auto">
          <NavSection
            label="Workspace"
            items={mainItems}
            pathname={location.pathname}
            collapsed={sidebarCollapsed}
          />
          <NavSection
            label="Tools"
            items={toolItems}
            pathname={location.pathname}
            collapsed={sidebarCollapsed}
          />
          <NavSection
            label="Account"
            items={settingsItems}
            pathname={location.pathname}
            collapsed={sidebarCollapsed}
          />
        </nav>

        {/* Collapse toggle (desktop only) */}
        <div className="hidden lg:flex border-t p-2">
          <Button
            variant="ghost"
            size="sm"
            className={cn('w-full gap-2 text-slate-500', sidebarCollapsed && 'justify-center')}
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          >
            {sidebarCollapsed ? (
              <ChevronsRight className="h-4 w-4" />
            ) : (
              <>
                <ChevronsLeft className="h-4 w-4" />
                <span>Collapse</span>
              </>
            )}
          </Button>
        </div>
      </div>
    </TooltipProvider>
  );

  return (
    <>
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar panel */}
      <aside
        className={cn(
          'fixed top-14 left-0 z-40 h-[calc(100vh-3.5rem)] shrink-0 border-r bg-background transition-all duration-200',
          // Mobile: slide in/out
          'lg:relative lg:top-0 lg:z-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
          // Desktop: collapsed width
          sidebarCollapsed ? 'w-16' : 'w-60'
        )}
      >
        {sidebarContent}
      </aside>
    </>
  );
}
